/**
 * Entity Type Registry
 * 
 * Centralized configuration for entity types that can be created from documents.
 * Defines the hierarchy of categories and subtypes, matching the manual creation flow.
 * 
 * @module entityTypeRegistry
 */

/**
 * Top-level entity categories
 * These map to document categories (travel, accommodation, location)
 */
export const ENTITY_CATEGORIES = {
    travel: {
        id: 'travel',
        label: 'Travel',
        icon: '🚀',
        documentCategory: 'travel',  // Maps from document primaryCategory
        subtypes: ['flight', 'train', 'bus', 'car', 'cruise', 'ferry', 'custom']
    },
    accommodation: {
        id: 'accommodation',
        label: 'Accommodation',
        icon: '🏠',
        documentCategory: 'accommodation',
        subtypes: ['hotel', 'apartment', 'house_villa', 'resort', 'hostel', 'custom']
    },
    place: {
        id: 'place',
        label: 'Place',
        icon: '📍',
        documentCategory: 'location',
        subtypes: []  // Direct creation, no subtypes yet
    }
};

/**
 * Entity subtypes with form and extraction configuration
 * Each subtype defines how to create and populate that entity type
 */
export const ENTITY_TYPES = {
    // Travel subtypes
    flight: {
        id: 'flight',
        label: 'Flight',
        icon: '✈️',
        category: 'travel',
        formComponent: 'AddTravel',
        formProps: { initialType: 'flight' },
        extractionPrompt: 'flightExtraction',
        enabled: true  // Phase 1
    },
    train: {
        id: 'train',
        label: 'Train',
        icon: '🚂',
        category: 'travel',
        formComponent: 'AddTravel',
        formProps: { initialType: 'train' },
        extractionPrompt: 'trainExtraction',
        enabled: false  // Phase 2
    },
    bus: {
        id: 'bus',
        label: 'Bus',
        icon: '🚌',
        category: 'travel',
        formComponent: 'AddTravel',
        formProps: { initialType: 'bus' },
        extractionPrompt: 'busExtraction',
        enabled: false  // Phase 2
    },
    car: {
        id: 'car',
        label: 'Car',
        icon: '🚗',
        category: 'travel',
        formComponent: 'AddTravel',
        formProps: { initialType: 'car' },
        extractionPrompt: 'carExtraction',
        enabled: true  // Enabled for car rental document extraction
    },
    cruise: {
        id: 'cruise',
        label: 'Cruise',
        icon: '🚢',
        category: 'travel',
        formComponent: 'AddTravel',
        formProps: { initialType: 'cruise' },
        extractionPrompt: 'cruiseExtraction',
        enabled: false  // Phase 2
    },
    ferry: {
        id: 'ferry',
        label: 'Ferry',
        icon: '⛴️',
        category: 'travel',
        formComponent: 'AddTravel',
        formProps: { initialType: 'ferry' },
        extractionPrompt: 'ferryExtraction',
        enabled: false  // Phase 2
    },

    // Accommodation subtypes
    hotel: {
        id: 'hotel',
        label: 'Hotel',
        icon: '🏨',
        category: 'accommodation',
        formComponent: 'AddAccommodation',
        formProps: { initialType: 'hotel' },
        extractionPrompt: 'hotelExtraction',
        enabled: true  // Phase 1
    },
    apartment: {
        id: 'apartment',
        label: 'Apartment',
        icon: '🏠',
        category: 'accommodation',
        formComponent: 'AddAccommodation',
        formProps: { initialType: 'apartment' },
        extractionPrompt: 'apartmentExtraction',
        enabled: false  // Phase 2
    },
    house_villa: {
        id: 'house_villa',
        label: 'House/Villa',
        icon: '🏡',
        category: 'accommodation',
        formComponent: 'AddAccommodation',
        formProps: { initialType: 'house_villa' },
        extractionPrompt: 'houseVillaExtraction',
        enabled: false  // Phase 2
    },
    resort: {
        id: 'resort',
        label: 'Resort',
        icon: '🏝️',
        category: 'accommodation',
        formComponent: 'AddAccommodation',
        formProps: { initialType: 'resort' },
        extractionPrompt: 'resortExtraction',
        enabled: false  // Phase 2
    },
    hostel: {
        id: 'hostel',
        label: 'Hostel',
        icon: '🛏️',
        category: 'accommodation',
        formComponent: 'AddAccommodation',
        formProps: { initialType: 'hostel' },
        extractionPrompt: 'hostelExtraction',
        enabled: false  // Phase 2
    }
};

/**
 * Get available entity types for a document category
 * Returns all types organized by category, with enabled/disabled status
 * 
 * @param {string} documentCategory - The document's primaryCategory
 * @returns {Array} Array of category objects with their subtypes
 */
export const getAvailableEntityTypes = (documentCategory) => {
    const result = [];

    // Determine which category should be suggested based on document category
    const suggestedCategoryId = Object.keys(ENTITY_CATEGORIES).find(
        catId => ENTITY_CATEGORIES[catId].documentCategory === documentCategory
    );

    // Build the hierarchical list
    Object.values(ENTITY_CATEGORIES).forEach(category => {
        const categoryTypes = category.subtypes.map(subtypeId => {
            const subtype = ENTITY_TYPES[subtypeId];
            if (!subtype) return null;

            return {
                ...subtype,
                isSuggested: category.id === suggestedCategoryId && subtypeId === category.subtypes[0]
            };
        }).filter(Boolean);

        if (categoryTypes.length > 0 || category.subtypes.length === 0) {
            result.push({
                ...category,
                types: categoryTypes,
                isSuggestedCategory: category.id === suggestedCategoryId
            });
        }
    });

    return result;
};

/**
 * Get the suggested entity type for a document category
 * 
 * @param {string} documentCategory - The document's primaryCategory
 * @returns {Object|null} The suggested entity type or null
 */
export const getSuggestedEntityType = (documentCategory) => {
    // Find the category that matches this document category
    const matchingCategory = Object.values(ENTITY_CATEGORIES).find(
        cat => cat.documentCategory === documentCategory
    );

    if (!matchingCategory || matchingCategory.subtypes.length === 0) {
        return null;
    }

    // Return the first enabled subtype, or the first subtype if none enabled
    const firstSubtype = matchingCategory.subtypes[0];
    return ENTITY_TYPES[firstSubtype] || null;
};

/**
 * Get entity type by ID
 * 
 * @param {string} entityTypeId - The entity type ID
 * @returns {Object|null} The entity type configuration
 */
export const getEntityType = (entityTypeId) => {
    return ENTITY_TYPES[entityTypeId] || null;
};

/**
 * Check if an entity type is enabled
 * 
 * @param {string} entityTypeId - The entity type ID
 * @returns {boolean} Whether the entity type is enabled
 */
export const isEntityTypeEnabled = (entityTypeId) => {
    const entityType = ENTITY_TYPES[entityTypeId];
    return entityType ? entityType.enabled : false;
};
